import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import {
  IWorkerPayslip, IWorkerQuoteRequest, INotification, IWorkerStat,
  ILoneWorkerSession, ILoneSosEvent,
} from "../../../types/api/payslips";

// ── Payslips ──────────────────────────────────────────────────────────────────

export const usePayslipList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.PAYSLIP_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.PAYSLIP_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IWorkerPayslip) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const usePayslipCreate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => { const res = await axios.post(API_ENDPOINTS.PAYSLIP_CREATE, data); return res.data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PAYSLIP_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

export const usePayslipDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => { const res = await axios.post(API_ENDPOINTS.PAYSLIP_DELETE, { id }); return res.data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PAYSLIP_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

// ── Worker Quote Requests ─────────────────────────────────────────────────────

export const useWorkerQuoteRequestList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.WORKER_QUOTE_REQUEST_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.WORKER_QUOTE_REQUEST_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IWorkerQuoteRequest) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useWorkerQuoteReview = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; status: "approved" | "rejected"; review_notes?: string }) => {
      const res = await axios.post(API_ENDPOINTS.WORKER_QUOTE_REQUEST_REVIEW, data);
      return res.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.WORKER_QUOTE_REQUEST_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const useNotificationList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.NOTIFICATION_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.NOTIFICATION_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: INotification) => ({ key: x.id, ...x })),
    }),
    initialData: { data: [], success: true, message: "", code: 200, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useNotificationRecipients = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.NOTIFICATION_RECIPIENTS],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.NOTIFICATION_RECIPIENTS); return res.data; },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useNotificationDispatch = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { user_ids: number[]; title: string; body?: string; type?: string }) => {
      const res = await axios.post(API_ENDPOINTS.NOTIFICATION_DISPATCH, data);
      return res.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.NOTIFICATION_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

// ── Worker Stats ──────────────────────────────────────────────────────────────

export const useWorkerStats = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.WORKER_STATS, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.WORKER_STATS, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IWorkerStat) => ({ key: x.id, ...x })),
    }),
    initialData: { data: [], success: true, message: "", code: 200, extra: {} },
    staleTime: 30000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

// ── Lone Worker ───────────────────────────────────────────────────────────────

export const useLoneWorkerSessions = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.LONE_WORKER_SESSIONS, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.LONE_WORKER_SESSIONS, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: ILoneWorkerSession) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 15000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useLoneWorkerSosList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.LONE_WORKER_SOS_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.LONE_WORKER_SOS_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: ILoneSosEvent) => ({ key: x.id, ...x })),
    }),
    initialData: { data: [], success: true, message: "", code: 200, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 15000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useLoneWorkerSosResolve = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; resolution_notes?: string }) => {
      const res = await axios.post(API_ENDPOINTS.LONE_WORKER_SOS_RESOLVE, data);
      return res.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.LONE_WORKER_SOS_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

// ── CIS (Construction Industry Scheme) ────────────────────────────────────────

export const useCISList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.CIS_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.CIS_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: any) => ({ key: x.id ?? x._id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 30000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

// ── NMW (National Minimum Wage) ───────────────────────────────────────────────

export const useNMWList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.NMW_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.NMW_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: any) => ({ key: x.id ?? x._id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 30000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useNMWRunCheck = (options?: { onSuccess?: (res: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { user_id: number; effective_rate: number; check_date?: string; age_bracket?: string; notes?: string }) => {
      const res = await axios.post(API_ENDPOINTS.NMW_RUN_CHECK, data);
      return res.data;
    },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.NMW_LIST] }); options?.onSuccess?.(res); },
    onError: options?.onError,
  });
};

export const useNMWBulkCheck = (options?: { onSuccess?: (res: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { check_date?: string; age_bracket?: string }) => {
      const res = await axios.post(API_ENDPOINTS.NMW_BULK_CHECK, data);
      return res.data;
    },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.NMW_LIST] }); options?.onSuccess?.(res); },
    onError: options?.onError,
  });
};

// ── Pension Auto-Enrolment ────────────────────────────────────────────────────

export const usePensionList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.PENSION_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.PENSION_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: any) => ({ key: x.id ?? x._id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 30000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const usePensionSettings = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.PENSION_SETTINGS],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.PENSION_SETTINGS); return res.data; },
    initialData: { data: null, success: true, message: "", code: 200 },
    staleTime: 30000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });

export const usePensionSettingsSave = (options?: { onSuccess?: (res: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { provider_name: string; scheme_name: string; employer_pct: number; employee_pct: number; staging_date?: string; re_enrolment_date?: string }) => {
      const res = await axios.post(API_ENDPOINTS.PENSION_SETTINGS, data);
      return res.data;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PENSION_SETTINGS] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PENSION_LIST] });
      options?.onSuccess?.(res);
    },
    onError: options?.onError,
  });
};

export const usePensionEnrol = (options?: { onSuccess?: (res: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { user_id: number; pension_setting_id: number; enrolment_date?: string }) => {
      const res = await axios.post(API_ENDPOINTS.PENSION_ENROL, data);
      return res.data;
    },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PENSION_LIST] }); options?.onSuccess?.(res); },
    onError: options?.onError,
  });
};

export const usePensionOptOut = (options?: { onSuccess?: (res: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.post(API_ENDPOINTS.PENSION_OPT_OUT(id));
      return res.data;
    },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PENSION_LIST] }); options?.onSuccess?.(res); },
    onError: options?.onError,
  });
};
